/**
 * Terrain streaming: chunk lifecycle, GPU upload, and the worker that keeps
 * meshing off the frame thread.
 *
 * The meshing itself lives in `mesher.ts` and is pure. This file only decides
 * what to build and when, hands the work to a worker, and uploads whatever
 * comes back.
 */

import { WORLD } from './config';
import { craterState, fieldRevision } from './field';
import { LAYERS, meshChunkLayer } from './mesher';
import { uploadMesh, type GpuMesh, type MeshData } from '../render/mesh';
import type { BuildResult } from './meshWorker';

export { LAYERS, layerLift } from './mesher';
export type { LayerSpec } from './mesher';

export interface Chunk {
  index: number;
  x0: number;
  x1: number;
  meshes: (GpuMesh | null)[];
  /** Layers currently out with the worker. */
  building: boolean[];
  /**
   * Layers that have been meshed, whether or not they produced geometry. A
   * chunk of pure open water meshes to nothing, and without this the streamer
   * would see a null mesh and ask for it again every frame, forever.
   */
  done: boolean[];
  /** Layers built so far. A chunk is drawable from the first layer onward. */
  built: number;
  ready: boolean;
}

/** One layer of one chunk, waiting on the worker. */
interface Job {
  jobId: number;
  index: number;
  layer: number;
}

export class Terrain {
  private chunks = new Map<number, Chunk>();
  private pending: number[] = [];
  /** Overridden by the quality tier. */
  radius: number = WORLD.radius;
  /** Milliseconds of synchronous meshing allowed per call, when there is no
   *  worker to do it. Ignored on the worker path, where meshing is free. */
  msBudget = 9;

  private worker: Worker | null = null;
  private inFlight = new Map<number, Job>();
  private nextJob = 1;
  /** Field revision the worker has been told about. */
  private syncedRevision = -1;
  /**
   * How many layers may be in the worker at once. Enough to keep it busy while
   * the main thread runs a frame, few enough that a burst of movement does not
   * queue up work for ground the player has already left.
   */
  private maxInFlight = 3;

  constructor(private device: GPUDevice) {
    this.startWorker();
  }

  private startWorker() {
    try {
      this.worker = new Worker(new URL('./meshWorker.ts', import.meta.url), { type: 'module' });
      this.worker.onmessage = (e: MessageEvent<BuildResult>) => this.onMesh(e.data);
      // A worker that dies takes streaming with it, so fall back rather than
      // leaving the player in an empty ocean.
      this.worker.onerror = () => {
        console.warn('[abyss] mesh worker failed; meshing on the main thread');
        this.worker?.terminate();
        this.worker = null;
        this.inFlight.clear();
      };
    } catch {
      this.worker = null;
    }
  }

  chunkAt(x: number): number {
    return Math.floor(x / WORLD.chunkSize);
  }

  /** Mirror the player's craters into the worker before asking it for anything. */
  private syncField() {
    if (!this.worker || this.syncedRevision === fieldRevision) return;
    this.syncedRevision = fieldRevision;
    this.worker.postMessage({ type: 'craters', data: craterState() });
  }

  private onMesh(r: BuildResult) {
    const job = this.inFlight.get(r.jobId);
    this.inFlight.delete(r.jobId);
    if (!job) return;
    const chunk = this.chunks.get(r.index);
    if (!chunk) return;

    // Built against a field the player has since blown a hole in — drop it and
    // let the normal streaming ask again.
    if (r.revision !== fieldRevision) {
      chunk.building[r.layer] = false;
      chunk.done[r.layer] = false;
      return;
    }

    const data: MeshData = {
      vertices: r.vertices, indices: r.indices,
      sliceY: r.sliceY, sliceStart: r.sliceStart,
    };
    const mesh = data.indices.length
      ? uploadMesh(this.device, data, `chunk${r.index}:L${r.layer}`)
      : null;
    const old = chunk.meshes[r.layer];
    chunk.meshes[r.layer] = mesh;
    if (old) { old.vbo.destroy(); old.ibo.destroy(); }
    chunk.building[r.layer] = false;
    chunk.done[r.layer] = true;
    chunk.built = chunk.done.filter(Boolean).length;
    chunk.ready = chunk.done.every(Boolean);
  }

  private request(chunk: Chunk, layer: number): boolean {
    if (chunk.building[layer]) return false;
    if (!this.worker) {
      // No worker: mesh here and now.
      const data = meshChunkLayer(chunk.index, layer, this.occupied);
      const old = chunk.meshes[layer];
      chunk.meshes[layer] = data.indices.length
        ? uploadMesh(this.device, data, `chunk${chunk.index}:L${layer}`)
        : null;
      if (old) { old.vbo.destroy(); old.ibo.destroy(); }
      chunk.done[layer] = true;
      chunk.built = chunk.done.filter(Boolean).length;
      chunk.ready = chunk.done.every(Boolean);
      return true;
    }
    if (this.inFlight.size >= this.maxInFlight) return false;
    this.syncField();
    const jobId = this.nextJob++;
    chunk.building[layer] = true;
    this.inFlight.set(jobId, { jobId, index: chunk.index, layer });
    this.worker.postMessage({
      type: 'build', jobId, index: chunk.index, layer,
      occupied: this.occupied, revision: fieldRevision,
    });
    return true;
  }

  private make(i: number): Chunk {
    const chunk: Chunk = {
      index: i,
      x0: i * WORLD.chunkSize,
      x1: (i + 1) * WORLD.chunkSize,
      meshes: new Array(LAYERS.length).fill(null),
      building: new Array(LAYERS.length).fill(false),
      done: new Array(LAYERS.length).fill(false),
      built: 0,
      ready: false,
    };
    this.chunks.set(i, chunk);
    return chunk;
  }

  /**
   * Keep chunks near `centerX` resident, nearest first, play layer before the
   * parallax layers. With a worker this only posts messages, so it costs the
   * frame nothing however expensive the ground is; the `budget` and `msBudget`
   * only bite on the synchronous fallback.
   */
  update(centerX: number, budget = 1): number {
    const started = performance.now();
    const c = this.chunkAt(centerX);
    const r = this.radius;
    this.pending.length = 0;
    for (let d = 0; d <= r; d++) {
      for (const s of d === 0 ? [0] : [-d, d]) {
        const i = c + s;
        const chunk = this.chunks.get(i);
        if (!chunk || chunk.built < LAYERS.length) this.pending.push(i);
      }
    }

    let done = 0;
    const cap = this.worker ? this.maxInFlight : budget;
    outer: for (const i of this.pending) {
      const chunk = this.chunks.get(i) ?? this.make(i);
      for (let layer = 0; layer < LAYERS.length; layer++) {
        if (chunk.done[layer] || chunk.building[layer]) continue;
        if (done >= cap) break outer;
        if (!this.request(chunk, layer)) break outer;
        done++;
        // Synchronous fallback only: always finish the layer in hand, but do
        // not start another once the frame's meshing budget is spent.
        if (!this.worker && performance.now() - started > this.msBudget) break outer;
      }
    }

    // Evict distant chunks.
    for (const [i, chunk] of this.chunks) {
      if (Math.abs(i - c) > r + 2) {
        // Anything still in the worker for this chunk will be dropped on
        // arrival because the chunk is gone.
        for (const m of chunk.meshes) {
          if (m) { m.vbo.destroy(); m.ibo.destroy(); }
        }
        this.chunks.delete(i);
      }
    }
    return done;
  }

  /**
   * Re-mesh every chunk overlapping [x0, x1] against the current field. Called
   * after an explosion carves rock. The old mesh stays on screen until the new
   * one arrives, so a blast never leaves a hole in the world.
   */
  /**
   * The lane the sub is flying, which is meshed at full detail rather than at
   * its parallax cell size. Changing it rebuilds the lane being left and the
   * lane being entered, and nothing else.
   */
  private occupied = 0;

  setOccupied(lane: number) {
    if (lane === this.occupied) return;
    const was = this.occupied;
    this.occupied = lane;
    for (const chunk of this.chunks.values()) {
      for (const li of [was, lane]) {
        if (li < 0 || li >= LAYERS.length) continue;
        chunk.done[li] = false;
        chunk.building[li] = false;
      }
      chunk.built = chunk.done.filter(Boolean).length;
      chunk.ready = chunk.done.every(Boolean);
    }
  }

  invalidate(x0: number, x1: number) {
    const a = this.chunkAt(x0 - 2);
    const b = this.chunkAt(x1 + 2);
    // The OCCUPIED lane, not layer 0. A crater is punched into the shared
    // field, so it surfaces in every layer that samples that part of it — but
    // each layer samples a different part, hundreds of metres apart, and the
    // span asked for here is in the drawn x of the lane that did the carving.
    // Rebuilding layer 0 for a hole blown on lane 2 rebuilt the wrong ground in
    // the wrong place and left the crater the player had just made undrawn.
    const layer = this.occupied;
    for (let i = a; i <= b; i++) {
      const chunk = this.chunks.get(i);
      if (!chunk || !chunk.done[layer]) continue;
      chunk.building[layer] = false;
      this.request(chunk, layer);
    }
  }

  /** Drop every chunk. Used when a new run resets the field. */
  clear() {
    for (const chunk of this.chunks.values()) {
      for (const m of chunk.meshes) {
        if (m) { m.vbo.destroy(); m.ibo.destroy(); }
      }
    }
    this.chunks.clear();
    this.inFlight.clear();
  }

  /** True once the play layer around `x` exists — used to gate the first frame. */
  readyAround(x: number, span = 2): boolean {
    const c = this.chunkAt(x);
    for (let i = c - span; i <= c + span; i++) {
      const chunk = this.chunks.get(i);
      if (!chunk || !chunk.done[0]) return false;
    }
    return true;
  }

  /** Visible chunks for a camera x span, nearest-first. */
  visible(minX: number, maxX: number): Chunk[] {
    const out: Chunk[] = [];
    const a = this.chunkAt(minX) - 1;
    const b = this.chunkAt(maxX) + 1;
    for (let i = a; i <= b; i++) {
      const c = this.chunks.get(i);
      if (c) out.push(c);
    }
    return out;
  }
}
